import type { Workflow, WorkflowLog } from '@lighthouse/shared'
import { useAtomAsyncAction, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import { useMount } from 'react-use'
import { useImmer } from 'use-immer'

import { fetchWorkflowAtom } from '@/atoms/workflow/action'
import { workflowListAtom } from '@/atoms/workflow/state'
import type { FlowDetailShape, FlowModePayload } from '@/contexts/FlowContext'
import * as srv from '@/services'

import { FlowEditor, type FlowEditorRef } from './FlowEditor'

export interface FlowDetailRef {
    requestClose: () => void
}

interface FlowDetailProps {
    id: string
    shape: FlowDetailShape
    scale?: number
    onBack?: () => void
    onDismiss?: () => void
    onClose?: () => void
}

interface FlowDetailState {
    log?: WorkflowLog
    mode: 'log' | 'editor'
}

export const FlowDetail = forwardRef<FlowDetailRef, FlowDetailProps>(({ id, shape, scale, onDismiss, onClose }, ref) => {
    const firstRef = useRef(true)
    const editorRef = useRef<FlowEditorRef>(null)
    const { run: fetchWorkflow, loading } = useAtomAsyncAction(fetchWorkflowAtom)
    const workflow = useAtomData(
        workflowListAtom,
        useCallback(workflows => find(item => item.id === id, workflows), [id])
    )

    const [state, setState] = useImmer<FlowDetailState>({
        mode: 'editor'
    })

    const { log, mode } = state

    useImperativeHandle(
        ref,
        () => ({
            requestClose: () => {
                if (editorRef.current) {
                    editorRef.current.requestClose()
                    return
                }

                onDismiss?.() ?? onClose?.()
            }
        }),
        [onClose, onDismiss]
    )

    useMount(() => {
        if (!log) {
            fetchWorkflow(id)
            firstRef.current = false
        }
    })

    const handleFlowChange = useCallback(
        async (val: FlowModePayload) => {
            if (val.mode === 'log') {
                const log = await srv.getFlowLog(val.logId)
                setState({
                    mode: val.mode,
                    log
                })
                return
            }
            setState({
                mode: 'editor'
            })
        },
        [setState]
    )

    const data: Workflow | undefined = useMemo(() => {
        if (mode === 'log') {
            if (!log || !workflow) {
                return
            }
            return {
                ...workflow,
                content: log.content
            }
        }
        return workflow
    }, [log, mode, workflow])

    if (!data || loading || firstRef.current) {
        return null
    }

    return (
        <FlowEditor
            ref={editorRef}
            key={log?.instanceId || id}
            data={data}
            name={workflow?.name}
            shape={shape}
            scale={scale}
            log={log}
            onClose={onClose}
            onFlowChange={handleFlowChange}
        />
    )
})

FlowDetail.displayName = 'FlowDetail'
